import { browserStorageOwnerIsCurrent, currentBrowserStorageOwner } from "./browserStorageScope.js";
import { onAuthIdentityChange } from "./authTokenStore.js";

const DB_NAME = "origin-control-proposals-v1";
const MAX_DRAFTS = 100;
const MAX_BYTES = 400_000;
const entries = new Map();
const loads = new Map();
const listeners = new Set();
let database;
let channel;
const EMPTY = Object.freeze({ status: "empty", draft: null, error: "" });
const keyFor = (owner, investigationId) => `${owner.key}:${investigationId}`;
const emit = () => { for (const fn of listeners) fn(); };

function publish(key, patch) {
    entries.set(key, { ...(entries.get(key) || EMPTY), ...patch });
    emit();
}

function db() {
    if (!database) database = new Promise((resolve, reject) => {
        const request = indexedDB.open(DB_NAME, 1);
        request.onupgradeneeded = () => {
            const store = request.result.createObjectStore("drafts", { keyPath: "key" });
            store.createIndex("owner", "owner");
        };
        request.onsuccess = () => resolve(request.result);
        request.onerror = () => reject(new Error("Browser storage is unavailable. Your changes are only in this tab."));
        request.onblocked = () => reject(new Error("Close other Origin tabs and retry saving this draft."));
    }).catch((error) => { database = null; throw error; });
    return database;
}

function listenAcrossTabs() {
    if (channel || typeof BroadcastChannel !== "function") return;
    channel = new BroadcastChannel(DB_NAME);
    channel.unref?.();
    channel.onmessage = ({ data }) => {
        const owner = currentBrowserStorageOwner();
        if (!owner || data?.owner !== owner.key) return;
        if (data.investigationId === "*") {
            for (const key of entries.keys()) if (key.startsWith(`${owner.key}:`)) publish(key, {
                status: "conflict", error: "Local proposals were cleared in another tab. Reload the proposal.",
            });
            return;
        }
        const key = keyFor(owner, data.investigationId);
        const known = entries.get(key);
        if (known && known.draft?.revision !== data.revision) publish(key, {
            status: "conflict", error: "This proposal changed in another tab. Reload the proposal before editing.",
        });
    };
}

export function subscribePolicyDrafts(fn) {
    listeners.add(fn);
    listenAcrossTabs();
    return () => listeners.delete(fn);
}

export function policyDraftState(investigationId) {
    const owner = currentBrowserStorageOwner();
    return owner && investigationId ? entries.get(keyFor(owner, investigationId)) || EMPTY : EMPTY;
}

export function markPolicyDraftPending(owner, investigationId) {
    if (!browserStorageOwnerIsCurrent(owner)) return;
    const key = keyFor(owner, investigationId);
    if (entries.get(key)?.status !== "conflict") publish(key, { status: "saving", error: "" });
}

export function loadPolicyDraft(investigationId, { force = false } = {}) {
    const owner = currentBrowserStorageOwner();
    if (!owner || !investigationId) return Promise.resolve(null);
    const key = keyFor(owner, investigationId);
    if (loads.has(key)) return loads.get(key);
    if (!force && entries.has(key)) return Promise.resolve(entries.get(key).draft);
    const pending = readDraft(owner, investigationId).finally(() => { if (loads.get(key) === pending) loads.delete(key); });
    loads.set(key, pending);
    return pending;
}

async function readDraft(owner, investigationId) {
    const key = keyFor(owner, investigationId);
    publish(key, { status: "loading", error: "" });
    try {
        const connection = await db();
        const draft = await new Promise((resolve, reject) => {
            const request = connection.transaction("drafts").objectStore("drafts").get(key);
            request.onsuccess = () => resolve(request.result || null);
            request.onerror = () => reject(request.error);
        });
        if (!browserStorageOwnerIsCurrent(owner)) return null;
        if (draft && (![1, 2].includes(draft.schemaVersion) || draft.owner !== owner.key || draft.investigationId !== investigationId)) {
            throw new Error("This local proposal cannot be read by this version of Origin.");
        }
        publish(key, { draft, status: draft ? "saved" : "empty", error: "" });
        return draft;
    } catch (error) {
        if (browserStorageOwnerIsCurrent(owner)) publish(key, { status: "error", error: error.message || "Could not read this browser's proposal. Retry." });
        return null;
    }
}

export async function savePolicyDraftLocally(owner, draft, expectedRevision) {
    if (!browserStorageOwnerIsCurrent(owner)) throw new Error("Your sign-in changed. Reopen the proposal.");
    const key = keyFor(owner, draft.investigationId);
    // Old clients must refuse independent proposals rather than resume them as empty wizard answers.
    const next = { ...draft, key, owner: owner.key, schemaVersion: draft.proposalState ? 2 : 1, revision: expectedRevision + 1, updatedAt: Date.now() };
    publish(key, { status: "saving", error: "" });
    try {
        if (new TextEncoder().encode(JSON.stringify(next)).length > MAX_BYTES) throw new Error("This proposal is too large to save in this browser.");
        const connection = await db();
        await new Promise((resolve, reject) => {
            const tx = connection.transaction("drafts", "readwrite");
            const store = tx.objectStore("drafts");
            let failure;
            const fail = (message) => { failure = new Error(message); tx.abort(); };
            const read = store.get(key);
            read.onsuccess = () => {
                if (!browserStorageOwnerIsCurrent(owner)) return fail("Your sign-in changed. Reopen the proposal.");
                if ((read.result?.revision || 0) !== expectedRevision) return fail("This proposal changed in another tab. Reload the proposal before editing.");
                if (read.result) store.put(next);
                else {
                    const count = store.index("owner").count(owner.key);
                    count.onsuccess = () => {
                        if (count.result >= MAX_DRAFTS) fail("This browser has 100 proposals. Discard an old proposal before saving another.");
                        else store.put(next);
                    };
                }
            };
            tx.oncomplete = resolve;
            tx.onabort = () => reject(failure || tx.error || new Error("Could not save this proposal. Retry."));
            tx.onerror = () => {};
        });
        if (!browserStorageOwnerIsCurrent(owner)) return next;
        publish(key, { draft: next, status: "saved", error: "" });
        channel?.postMessage({ owner: owner.key, investigationId: draft.investigationId, revision: next.revision });
        return next;
    } catch (error) {
        if (browserStorageOwnerIsCurrent(owner)) publish(key, {
            status: error.message.includes("another tab") ? "conflict" : "error",
            error: error.message || "Could not save this proposal. Retry.",
        });
        throw error;
    }
}

export async function discardPolicyDraft(investigationId) {
    const owner = currentBrowserStorageOwner();
    if (!owner) return;
    const key = keyFor(owner, investigationId);
    const expectedRevision = entries.get(key)?.draft?.revision || 0;
    const connection = await db();
    await new Promise((resolve, reject) => {
        const tx = connection.transaction("drafts", "readwrite");
        if (!browserStorageOwnerIsCurrent(owner)) return reject(new Error("Your sign-in changed. Reopen the proposal."));
        let failure;
        const store = tx.objectStore("drafts");
        const read = store.get(key);
        read.onsuccess = () => {
            if (!browserStorageOwnerIsCurrent(owner) || (read.result?.revision || 0) !== expectedRevision) {
                failure = new Error("The proposal changed. Reload it before discarding."); tx.abort();
            } else store.delete(key);
        };
        tx.oncomplete = resolve;
        tx.onabort = () => reject(failure || tx.error);
    });
    if (browserStorageOwnerIsCurrent(owner)) {
        entries.delete(key); emit();
        channel?.postMessage({ owner: owner.key, investigationId, revision: null });
    }
}

export async function clearPolicyDrafts(tenantId) {
    const owner = currentBrowserStorageOwner(tenantId);
    if (!owner) return;
    const connection = await db();
    await new Promise((resolve, reject) => {
        const tx = connection.transaction("drafts", "readwrite");
        if (!browserStorageOwnerIsCurrent(owner)) return reject(new Error("Your sign-in changed."));
        const cursor = tx.objectStore("drafts").index("owner").openCursor(IDBKeyRange.only(owner.key));
        cursor.onsuccess = () => { if (cursor.result) { cursor.result.delete(); cursor.result.continue(); } };
        tx.oncomplete = resolve;
        tx.onabort = () => reject(tx.error);
    });
    for (const key of entries.keys()) if (key.startsWith(`${owner.key}:`)) entries.delete(key);
    channel?.postMessage({ owner: owner.key, investigationId: "*", revision: null });
    emit();
}

onAuthIdentityChange(() => { entries.clear(); loads.clear(); emit(); });
